import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_URL } from "@/lib/api";
import { fmtCurrency, fmtHours, fmtPct, clientCurrency } from "@/lib/calc";
import { trackEvent } from "@/lib/track";
import { useBilling } from "@/context/BillingContext";
import MetricCard from "@/components/MetricCard";
import StatusPill from "@/components/StatusPill";
import UtilizationChart from "@/components/UtilizationChart";
import HealthBreakdown from "@/components/HealthBreakdown";
import FlagsPanel from "@/components/FlagsPanel";
import { useLayout } from "@/components/AppLayout";
import {
  DollarSign,
  Clock,
  Gauge,
  TrendingUp,
  ChevronRight,
  RefreshCw,
  Download,
  Camera,
  History,
} from "lucide-react";
import { toast } from "sonner";

const Dashboard = () => {
  const [data, setData] = useState({
    metrics: null,
    clients: [],
    prev_metrics: null,
    prev_period: null,
  });
  const [digest, setDigest] = useState({ flags: [], wins: [] });
  const [settings, setSettings] = useState({ currency: "USD" });
  const [loading, setLoading] = useState(true);
  const { subscription } = useBilling();
  const isPaid = subscription?.status === "active";
  const [snapping, setSnapping] = useState(false);
  const { refreshKey, openAddClient } = useLayout();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [d, dig, s] = await Promise.all([
        api.get("/dashboard"),
        api.get("/digest"),
        api.get("/settings"),
      ]);
      setData(d.data);
      setDigest(dig.data);
      setSettings(s.data);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    trackEvent("dashboard_viewed");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleResetDemo = async () => {
    const count = data?.clients?.length || 0;
    const proceed = window.confirm(
      count > 0
        ? `Reset will replace your current ${count} retainer${count === 1 ? "" : "s"} with the 5 sample clients. This can't be undone. Continue?`
        : "Load the 5 sample clients into your dashboard?"
    );
    if (!proceed) return;
    try {
      await api.post("/clients/reset-demo");
      toast.success("Demo data reset");
      load();
    } catch {
      toast.error("Failed to reset demo");
    }
  };

  const handleExport = () => {
    const link = document.createElement("a");
    link.href = `${API_URL}/clients/export`;
    link.target = "_self";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSnapshot = async () => {
    setSnapping(true);
    try {
      await api.post("/snapshots");
      toast.success("Snapshot saved");
      await load();
    } catch {
      toast.error("Could not save snapshot");
    } finally {
      setSnapping(false);
    }
  };

  const delta = (curr, prev) => {
    if (prev === null || prev === undefined) return null;
    if (prev === 0) return curr === 0 ? 0 : null;
    return ((curr - prev) / prev) * 100;
  };

  const m = data.metrics;
  const p = data.prev_metrics;
  const currency = settings?.currency || "USD";

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Action row */}
      <div className="flex flex-wrap items-center gap-2 -mt-2">
        <button
          type="button"
          onClick={handleSnapshot}
          disabled={snapping}
          data-testid="dashboard-snapshot-btn"
          className="text-xs text-[#1F2326] bg-white border border-[#E4DFD2] hover:bg-[#FBFAF6] hover:border-[#D6CFB9] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-60"
          title="Save this month's numbers"
        >
          <Camera className="w-3.5 h-3.5" />
          {snapping ? "Saving..." : "Take snapshot"}
        </button>
        <button
          type="button"
          onClick={() => navigate("/snapshots")}
          data-testid="dashboard-snapshots-link"
          className="text-xs text-[#69747C] hover:text-[#1F2326] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-[#F3EFE3]"
        >
          <History className="w-3.5 h-3.5" /> View snapshots
        </button>
        <button
          type="button"
          onClick={handleExport}
          data-testid="dashboard-export-btn"
          className="text-xs text-[#1F2326] bg-white border border-[#E4DFD2] hover:bg-[#FBFAF6] hover:border-[#D6CFB9] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition-colors"
          title="Download all clients as CSV"
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
        <button
          type="button"
          onClick={handleResetDemo}
          data-testid="reset-demo-btn"
          className={`text-xs text-[#69747C] hover:text-[#1F2326] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-[#F3EFE3] ${isPaid ? "hidden" : ""}`}
          title="Replace data with demo set"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Reset demo
        </button>
        {p ? (
          <div className="ml-auto text-[11px] text-[#69747C] uppercase tracking-wider font-semibold">
            Δ vs {data.prev_period}
          </div>
        ) : null}
      </div>

      {/* Top financial cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Retainer Revenue"
          value={loading ? "." : fmtCurrency(m?.total_revenue || 0, currency)}
          sub={`Across ${m?.total_clients || 0} retainers`}
          icon={DollarSign}
          testid="metric-revenue"
          delta={m && p ? delta(m.total_revenue, p.total_revenue) : null}
        />
        <MetricCard
          label="Logged Hours"
          value={loading ? "." : fmtHours(m?.total_logged_hours || 0)}
          sub={`of ${fmtHours(m?.total_included_hours || 0)} included`}
          icon={Clock}
          testid="metric-hours"
          delta={m && p ? delta(m.total_logged_hours, p.total_logged_hours) : null}
        />
        <MetricCard
          label="Average Utilization"
          value={loading ? "." : fmtPct(m?.avg_utilization || 0)}
          sub="Of included hours"
          icon={Gauge}
          testid="metric-utilization"
          delta={m && p ? delta(m.avg_utilization, p.avg_utilization) : null}
        />
        <MetricCard
          label="Estimated Profit"
          value={loading ? "." : fmtCurrency(m?.gross_profit || 0, currency)}
          sub="Revenue minus delivery cost"
          icon={TrendingUp}
          accent={
            (m?.gross_profit || 0) >= 0 ? "text-[#42624B]" : "text-[#7E332D]"
          }
          testid="metric-profit"
          delta={m && p ? delta(m.gross_profit, p.gross_profit) : null}
        />
      </div>

      {/* Retainer value cards — earned to date, value left, value used %, over-hours */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4" data-testid="earned-to-date-row">
        <MetricCard
          label="Earned to Date"
          value={loading ? "." : fmtCurrency(m?.total_earned_to_date || 0, currency)}
          sub="Value delivered so far"
          icon={DollarSign}
          testid="metric-earned"
        />
        <MetricCard
          label="Value Left"
          value={loading ? "." : fmtCurrency(m?.total_value_left || 0, currency)}
          sub="Remaining retainer value"
          icon={DollarSign}
          testid="metric-value-left"
        />
        <MetricCard
          label="Retainer Value Used"
          value={loading ? "." : `${fmtPct(m?.avg_value_used_pct || 0)}`}
          sub="Across all retainers"
          icon={Gauge}
          testid="metric-value-used"
        />
        <MetricCard
          label="Hours Over"
          value={loading ? "." : fmtHours(m?.total_overage_hours || 0)}
          sub={(m?.total_overage_hours || 0) > 0 ? "Past included hours" : "All within limits"}
          icon={Clock}
          accent={(m?.total_overage_hours || 0) > 0 ? "text-[#7E332D]" : ""}
          testid="metric-overage"
        />
      </div>

      {/* Multi-currency breakdown — only renders when client mix uses 2+ currencies */}
      {(m?.currency_breakdown || []).filter((b) => b.currency !== "DEFAULT").length > 1 ? (
        <div
          data-testid="currency-breakdown"
          className="bg-white border border-[#E4DFD2] rounded-xl p-5 mt-4"
        >
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#69747C] mb-3">
            Per-currency totals
          </div>
          <div className="text-[11px] text-[#69747C] mb-3">
            Top-card totals sum raw numbers across currencies. Use this breakdown for the true picture per currency.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {m.currency_breakdown
              .filter((b) => b.currency !== "DEFAULT")
              .map((b) => (
                <div key={b.currency} className="border border-[#EDE7D6] rounded-lg px-3 py-2.5">
                  <div className="text-[11px] font-semibold tracking-wider text-[#B89B5E]">
                    {b.currency} · {b.count} {b.count === 1 ? "retainer" : "retainers"}
                  </div>
                  <div className="font-display text-xl font-semibold text-[#1F2326] mt-1">
                    {fmtCurrency(b.revenue, b.currency)}
                  </div>
                  <div className="text-xs text-[#69747C] mt-0.5">
                    Profit{" "}
                    <span className={b.profit >= 0 ? "text-[#42624B] font-medium" : "text-[#7E332D] font-medium"}>
                      {fmtCurrency(b.profit, b.currency)}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : null}

      {/* Status count cards */}
      <div className="grid grid-cols-3 gap-4">
        <MetricCard
          label="On Track"
          value={loading ? "." : `${m?.on_track_count || 0}`}
          sub="Within hours and margin"
          accent="text-[#42624B]"
          testid="metric-on-track"
        />
        <MetricCard
          label="Close to Limit"
          value={loading ? "." : `${m?.close_to_limit_count || 0}`}
          sub="Near hours or margin"
          accent="text-[#7A5E1F]"
          testid="metric-close-limit"
        />
        <MetricCard
          label="Losing Money"
          value={loading ? "." : `${m?.losing_money_count || 0}`}
          sub="Over hours or low margin"
          accent="text-[#7E332D]"
          testid="metric-losing"
        />
      </div>

      {/* Chart + breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E4DFD2] rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C]">
                Utilization by client
              </div>
              <div className="font-display text-lg font-semibold text-[#1F2326]">
                Hours logged vs. included
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4 text-xs text-[#69747C]">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#6F9775]" /> ≤85%
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#C99A4A]" /> 86 to 100%
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#C76055]" /> over 100%
              </span>
            </div>
          </div>
          <UtilizationChart clients={data.clients} />
        </div>
        <HealthBreakdown metrics={m} />
      </div>

      {/* Flags + table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <FlagsPanel digest={digest} loading={loading} />

        <div className="lg:col-span-2 bg-white border border-[#E4DFD2] rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#EDE7D6]">
            <div>
              <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C]">
                Retainer clients
              </div>
              <div className="font-display text-lg font-semibold text-[#1F2326]">
                All retainers, this month
              </div>
            </div>
            <button
              type="button"
              onClick={() => openAddClient()}
              data-testid="dashboard-add-client-btn"
              className="text-xs text-[#F8F6F1] bg-[#1F2326] hover:bg-[#2A2E32] px-3 py-1.5 rounded-lg font-medium"
            >
              + Add retainer
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead>
                <tr className="text-[11px] tracking-wider uppercase text-[#69747C] bg-[#FBFAF6]/60">
                  <th className="text-left font-semibold py-3 px-6">Client</th>
                  <th className="text-right font-semibold py-3 px-3">Utilization</th>
                  <th className="text-right font-semibold py-3 px-3">Profit</th>
                  <th className="text-left font-semibold py-3 px-3">Status</th>
                  <th className="py-3 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-sm text-[#69747C]">
                      Loading retainers.
                    </td>
                  </tr>
                )}
                {!loading && data.clients.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="text-[#69747C] text-sm">No retainers yet</div>
                      <button
                        onClick={() => openAddClient()}
                        data-testid="empty-add-client-btn"
                        className="mt-3 text-[#B89B5E] hover:text-[#A78946] text-sm font-medium"
                      >
                        + Add your first retainer
                      </button>
                    </td>
                  </tr>
                )}
                {!loading &&
                  data.clients.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => navigate(`/retainers/${c.id}`)}
                      className="border-t border-[#EDE7D6] hover:bg-[#FBFAF6]/60 cursor-pointer transition-colors"
                      data-testid={`client-row-${c.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#1F2326]">{c.name}</div>
                        <div className="text-xs text-[#69747C] mt-0.5 tabular-nums">
                          {fmtHours(c.logged_hours)} of {fmtHours(c.included_hours)}
                          {c.service_type ? <span className="text-[#B89B5E]"> · {c.service_type}</span> : null}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-right tabular-nums text-[#1F2326]">
                        {fmtPct(c.utilization_pct)}
                      </td>
                      <td
                        className={`px-3 py-4 text-right tabular-nums font-medium ${
                          c.estimated_profit >= 0 ? "text-[#42624B]" : "text-[#7E332D]"
                        }`}
                      >
                        {fmtCurrency(c.estimated_profit, clientCurrency(c, currency))}
                      </td>
                      <td className="px-3 py-4">
                        <StatusPill status={c.status} />
                      </td>
                      <td className="px-3 py-4 text-right">
                        <ChevronRight className="w-4 h-4 text-[#C9C2B0] inline-block" />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
